import React, { useState, useEffect, useRef } from 'react';
import { login } from '../api/auth';
import RegisterModal from './RegisterModal';

function LoginModal({ show, onClose, onLoginSuccess }) {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [showRegister, setShowRegister] = useState(false);

    const formRef = useRef(null);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError('');
        setResult(null);
        setLoading(true);

        try {
            const authResult = await login(formData.email, formData.password);

            if (authResult.success) {
                setResult({
                    success: true,
                    message: authResult.message,
                    data: authResult.data
                });

                setFormData({
                    email: '',
                    password: ''
                });

                if (onLoginSuccess) {
                    onLoginSuccess(authResult.data);
                }

                setTimeout(() => {
                    if (formRef.current) {
                        const nativeSubmitEvent = new Event('submit', { cancelable: true, bubbles: true });
                        formRef.current.dispatchEvent(nativeSubmitEvent);
                    }
                    onClose();
                }, 1500);
            } else {
                setError(authResult.message);
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Login error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleShowRegister = () => {
        setShowRegister(true);
    };

    const handleRegistrationSuccess = () => {
        // Registration was successful, user can now login
        // We'll keep the registration modal open but with success message
    };

    const handleBackToLogin = () => {
        setShowRegister(false);
    };

    useEffect(() => {
        if (show) {
            setError('');
            setResult(null);
        }
    }, [show]);

    if (!show) return null;

    if (showRegister) {
        return (
            <RegisterModal
                show={true}
                onClose={onClose}
                onRegistrationSuccess={handleRegistrationSuccess}
                onSwitchToLogin={handleBackToLogin}
            />
        );
    }

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1040 }}
                onClick={onClose}
            ></div>

            {/* Modal dialog */}
            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 1050 }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    role="document"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Login</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>
                        </div>

                        {/* Modal body */}
                        <div className="modal-body">
                            {/* Status alerts */}
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            {result && result.success && (
                                <div className="alert alert-success" role="alert">
                                    {result.message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} noValidate ref={formRef}>
                                <div className="mb-3">
                                    <label htmlFor="email" className="form-label">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        id="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        disabled={loading}
                                        required
                                        autoComplete="email"
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="password" className="form-label">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        id="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        disabled={loading}
                                        required
                                        autoComplete="current-password"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn btn-primary w-100 mb-3"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Logging in...
                                        </>
                                    ) : (
                                        'Log In'
                                    )}
                                </button>

                                <div className="text-center">
                                    <p className="mb-0">
                                        Don't have an account?{' '}
                                        <button
                                            type="button"
                                            className="btn btn-link p-0"
                                            onClick={handleShowRegister}
                                        >
                                            Create one
                                        </button>
                                    </p>
                                </div>
                            </form>

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LoginModal;